import { useEffect, useRef, useState } from 'react';
import { eventBus } from '@/shared/EventBus';
import { WORLD_INTERACTION_INPUT } from '@/config/input.config';

/**
 * Wirtualny joystick do mobile.
 *
 *  - Aktywuje się na touchstart/mousedown w obszarze (np. lewa dolna ćwiartka).
 *  - Stick podąża za palcem, clamp'owany do promienia outer.
 *  - Emituje `input:joystick` z (dx, dy) ∈ [-1, 1] na ruch i (0, 0) na release.
 *
 * Stick znika gdy palec puszczony, a pojawia się po przekroczeniu progu ruchu
 * w miejscu touchstart (floating). Nieruchomy hold nad obiektem pokazuje dzięki
 * temu pierścień interakcji, a nie konkurującą grafikę joysticka.
 */
const RADIUS_OUTER = 60;   // px — promień zewnętrznego okręgu
const RADIUS_INNER = 28;   // px — promień gałki

export function Joystick() {
  const containerRef = useRef<HTMLDivElement>(null);
  const activeGestureRef = useRef<number | 'mouse' | null>(null);
  const [state, setState] = useState<{
    active: boolean;
    cx: number;   // środek joysticka (gdzie palec wystartował), px CSS
    cy: number;
    sx: number;   // pozycja stick'a, px CSS względem (cx, cy)
    sy: number;
  }>({ active: false, cx: 0, cy: 0, sx: 0, sy: 0 });

  // Wysyłanie eventu na zmianę state (tylko gdy aktywny).
  useEffect(() => {
    if (state.active) {
      const distance = Math.hypot(state.sx, state.sy);
      const strength = distance <= WORLD_INTERACTION_INPUT.joystickDeadZonePx
        ? 0
        : Math.min(
            1,
            (distance - WORLD_INTERACTION_INPUT.joystickDeadZonePx)
              / (RADIUS_OUTER - WORLD_INTERACTION_INPUT.joystickDeadZonePx),
          );
      const dx = distance > 0 ? (state.sx / distance) * strength : 0;
      const dy = distance > 0 ? (state.sy / distance) * strength : 0;
      eventBus.emit('input:joystick', { dx, dy });
    } else {
      eventBus.emit('input:joystick', { dx: 0, dy: 0 });
    }
  }, [state]);

  // Unmount podczas teleportu/cinematicu nie może zostawić ostatniego
  // niezerowego wektora w InputSystemie aktywnej jeszcze sceny.
  useEffect(() => () => {
    activeGestureRef.current = null;
    eventBus.emit('input:joystick', { dx: 0, dy: 0 });
  }, []);

  // Koniec gestu musi być nasłuchiwany STALE, nie dopiero po flushu stanu
  // Reacta. Inaczej bardzo szybkie touchstart→touchend mogłoby zostawić ref
  // na martwym identifierze i zablokować wszystkie kolejne starty joysticka.
  useEffect(() => {
    function endGesture(source: number | 'mouse'): void {
      if (activeGestureRef.current !== source) return;
      activeGestureRef.current = null;
      setState({ active: false, cx: 0, cy: 0, sx: 0, sy: 0 });
    }

    const touchEnd = (event: TouchEvent): void => {
      const id = activeGestureRef.current;
      if (typeof id !== 'number') return;
      const ended = Array.from(event.changedTouches).some(
        (touch) => touch.identifier === id,
      );
      if (ended) endGesture(id);
    };
    const mouseUp = (): void => endGesture('mouse');

    window.addEventListener('touchend', touchEnd);
    window.addEventListener('touchcancel', touchEnd);
    window.addEventListener('mouseup', mouseUp);
    return () => {
      window.removeEventListener('touchend', touchEnd);
      window.removeEventListener('touchcancel', touchEnd);
      window.removeEventListener('mouseup', mouseUp);
    };
  }, []);

  // HUD jest przezroczysty w obszarze joysticka, aby znajdujący się tam
  // obiekt świata nadal dostał ten sam gest. Start joysticka podsłuchujemy na
  // Window, ale wyłącznie dla zdarzeń, które faktycznie zaczęły się na
  // canvasie i w lewej dolnej ćwiartce. FAB, komunikaty i modale mają inny
  // `target`, więc nigdy nie uruchamiają ruchu awatara.
  useEffect(() => {
    function isJoystickArea(clientX: number, clientY: number, target: EventTarget | null): boolean {
      const canvas = document.querySelector<HTMLCanvasElement>('#game canvas');
      return (
        target === canvas &&
        clientX <= window.innerWidth / 2 &&
        clientY >= window.innerHeight / 2
      );
    }

    const touchStart = (event: TouchEvent): void => {
      if (activeGestureRef.current !== null) return;
      const touch = event.changedTouches[0];
      if (touch && isJoystickArea(touch.clientX, touch.clientY, event.target)) {
        start(touch.clientX, touch.clientY, touch.identifier);
      }
    };
    const mouseDown = (event: MouseEvent): void => {
      if (activeGestureRef.current !== null) return;
      if (isJoystickArea(event.clientX, event.clientY, event.target)) {
        start(event.clientX, event.clientY, 'mouse');
      }
    };

    window.addEventListener('touchstart', touchStart, { passive: true });
    window.addEventListener('mousedown', mouseDown);
    return () => {
      window.removeEventListener('touchstart', touchStart);
      window.removeEventListener('mousedown', mouseDown);
    };
  }, []);

  // Globalne listenery ruchu, żeby joystick działał poza swoim obszarem.
  useEffect(() => {
    if (!state.active) return;

    function onMove(clientX: number, clientY: number) {
      let dx = clientX - state.cx;
      let dy = clientY - state.cy;
      const dist = Math.hypot(dx, dy);
      if (dist > RADIUS_OUTER) {
        dx = (dx / dist) * RADIUS_OUTER;
        dy = (dy / dist) * RADIUS_OUTER;
      }
      setState((s) => ({ ...s, sx: dx, sy: dy }));
    }

    const touchMove = (e: TouchEvent) => {
      const id = activeGestureRef.current;
      if (typeof id !== 'number') return;
      const t = Array.from(e.touches).find(
        (touch) => touch.identifier === id,
      );
      if (t) onMove(t.clientX, t.clientY);
    };
    const mouseMove = (e: MouseEvent) => {
      if (activeGestureRef.current === 'mouse') onMove(e.clientX, e.clientY);
    };

    window.addEventListener('touchmove', touchMove, { passive: true });
    window.addEventListener('mousemove', mouseMove);

    return () => {
      window.removeEventListener('touchmove', touchMove);
      window.removeEventListener('mousemove', mouseMove);
    };
  }, [state.active, state.cx, state.cy]);

  function start(clientX: number, clientY: number, source: number | 'mouse') {
    activeGestureRef.current = source;
    setState({
      active: true,
      cx: clientX,
      cy: clientY,
      sx: 0,
      sy: 0,
    });
  }

  const joystickVisible =
    state.active &&
    Math.hypot(state.sx, state.sy) > WORLD_INTERACTION_INPUT.cancelDistancePx;

  return (
    <div
      ref={containerRef}
      className="absolute inset-0 touch-none pointer-events-none"
    >
      {joystickVisible && (
        <>
          <div
            className="absolute rounded-full bg-white/40 border-2 border-white/60 pointer-events-none"
            style={{
              left: state.cx - RADIUS_OUTER,
              top: state.cy - RADIUS_OUTER,
              width: RADIUS_OUTER * 2,
              height: RADIUS_OUTER * 2,
            }}
          />
          <div
            className="absolute rounded-full bg-white/85 border-2 border-meadow-700 shadow-lg pointer-events-none"
            style={{
              left: state.cx + state.sx - RADIUS_INNER,
              top: state.cy + state.sy - RADIUS_INNER,
              width: RADIUS_INNER * 2,
              height: RADIUS_INNER * 2,
            }}
          />
        </>
      )}
    </div>
  );
}
